/**
 * @fileoverview Initialises the Firebase SDK and exposes typed helper
 * utilities for authentication, Firestore access and other Firebase services
 * required by the application.
 */

import { initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getStorage } from "firebase/storage";
import type { User as FirebaseUser } from "firebase/auth";
import type { ProfileDoc } from "../lib/types";
import { firebaseConfig } from "./firebaseConfig";
import { clearPendingInvitationClaim } from "@/utils/pendingInvitationClaimStorage";
export { initAnalyticsLazy } from "@/analytics/firebaseAnalytics";

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
// Initialize Firebase
/** Shared Firebase application instance. */
export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
// Explicitly bind Storage to the configured bucket using gs:// URL to avoid domain ambiguity
const storageBucketUrl = firebaseConfig.storageBucket
  ? `gs://${firebaseConfig.storageBucket}`
  : undefined;
export const storage = storageBucketUrl
  ? getStorage(app, storageBucketUrl)
  : getStorage(app);

// Lazy App Check initializer to satisfy Storage/AppCheck enforcement
/**
 * Lazy initialiser for Firebase App Check that supports development friendly
 * debugging while satisfying production enforcement.
 */
export async function initAppCheckLazy() {
  if (typeof window === "undefined") return;
  try {
    const isDev = import.meta.env.DEV === true || /localhost|127\.0\.0\.1|\.localhost$/i.test(window.location.hostname);
    const enableDebug = String(import.meta.env.VITE_APPCHECK_DEBUG || (isDev ? 'true' : 'false')) === 'true';
    const siteKey = import.meta.env.VITE_APPCHECK_PUBLIC_KEY as string | undefined;

    // Enable debug token in dev if requested; Console will print the token for whitelisting
    if (enableDebug) {
      (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }

    // Initialize App Check only if we have a site key or debug is on
    if (siteKey || enableDebug) {
      const mod = await import('firebase/app-check');
      const provider = new mod.ReCaptchaV3Provider(siteKey || 'appcheck-debug');
      mod.initializeAppCheck(app, {
        provider,
        isTokenAutoRefreshEnabled: true,
      });
      console.log('[AppCheck] Initialized', { debug: enableDebug, hasSiteKey: !!siteKey });
    }
  } catch (e) {
    console.warn('[AppCheck] Initialization skipped:', e);
  }
}

// Create or update user record in Firestore
/**
 * Ensures a Firestore user document exists for the authenticated Firebase
 * user, creating one on first sign-in and updating metadata on subsequent
 * logins.
 */
export const createUserRecord = async (user: FirebaseUser | null) => {
  if (!user) return;

  const userRef = doc(db, "users", user.uid);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) {
    // Create new user document if it doesn't exist
    await setDoc(userRef, {
      email: user.email,
      displayName: user.displayName || "",
      photoURL: user.photoURL || "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Default values for new users
      role: "user",
    });
    return true;
  } else {
    // User already exists, just update the last login time
    await updateDoc(userRef, {
      lastLogin: serverTimestamp(),
    });
    return false;
  }
};

// Auth helpers with user record creation
/**
 * Creates a new Firebase Auth user and corresponding Firestore profile.
 */
export const signUp = async (email: string, password: string) => {
  const credential = await createUserWithEmailAndPassword(
    auth,
    email,
    password
  );
  await createUserRecord(credential.user);
  return credential;
};

/** Signs in an existing user and refreshes their Firestore profile metadata. */
export const signIn = async (email: string, password: string) => {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  await createUserRecord(credential.user);
  return credential;
};

/** Signs in a user via Google OAuth and ensures a profile document exists. */
export const signInWithGoogle = async () => {
  const credential = await signInWithPopup(auth, new GoogleAuthProvider());
  await createUserRecord(credential.user);
  return credential;
};

/** Signs the current user out of Firebase Auth. */
export const signOutUser = () => firebaseSignOut(auth);

/**
 * Performs a complete signout with comprehensive cleanup of all user data,
 * auth tokens, and storage while preserving user preferences (theme, analytics consent).
 * This includes:
 * - Clearing sessionStorage
 * - Clearing marketing persist data
 * - Firebase signout (triggers Redux cleanup via middleware)
 * - Preserving theme and analytics consent preferences
 */
export const performCompleteSignout = async () => {
  try {
    // 1. Preserve user preferences before cleanup
    const theme = localStorage.getItem('profilepro-theme');
    const analyticsConsent = localStorage.getItem('analytics-consent');

    // 2. Clear sessionStorage completely
    sessionStorage.clear();

    // 3. Clear marketing persist data
    localStorage.removeItem('persist:marketing');
    clearPendingInvitationClaim();

    // 4. Firebase signout - this will trigger Redux cleanup via middleware
    // The middleware will call persistor.purge() which clears persist:root
    await firebaseSignOut(auth);

    // 5. Restore preserved preferences
    if (theme) {
      localStorage.setItem('profilepro-theme', theme);
    }
    if (analyticsConsent) {
      localStorage.setItem('analytics-consent', analyticsConsent);
    }
  } catch (error) {
    console.error('Error during complete signout:', error);
    throw error;
  }
};

/** Sends a Firebase Auth password reset email to the supplied address. */
export const resetPassword = (email: string) =>
  firebaseSendPasswordResetEmail(auth, email);

/**
 * Refreshes the current user's profile and ID token.
 *
 * This is useful after email verification to ensure the client has an updated
 * token with the correct `email_verified` claim. Returns the updated user or
 * null if no user is signed in.
 */
export const refreshUserToken = async () => {
  const user = auth.currentUser;
  if (user) {
    await user.reload();
    await user.getIdToken(true);
  }
  return user;
};

// Firestore helpers
/** Retrieves a public profile document by username. */
export const getProfile = async (username: string) => {
  const profileRef = doc(db, "profiles", username);
  return getDoc(profileRef);
};

/** Fetches raw user data from the `users` collection by UID. */
export const getUserData = async (uid: string) => {
  const userRef = doc(db, "users", uid);
  return getDoc(userRef);
};

/**
 * Returns the username associated with the provided UID or `null` if one has
 * not been set.
 */
export const getUserProfileUsername = async (uid: string) => {
  try {
    const userRef = doc(db, "users", uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists() && userDoc.data().profileUsername) {
      return userDoc.data().profileUsername;
    }
    return null;
  } catch (error) {
    console.error("Error getting user profile username:", error);
    return null;
  }
};

// Direct client-side implementations of Firebase functions
type FunctionData =
  | { field: keyof ProfileDoc; value: string }
  | { userId: string }
  | { username: string }
  | { username: string; field: string; value: unknown };

/**
 * Limited wrapper that emulates callable Cloud Functions on the client by
 * dispatching to locally implemented handlers.
 */
export const callFunction = async (
  functionName: string,
  data: FunctionData
) => {
  switch (functionName) {
    case "checkSiteId":
      return checkSiteId((data as { username: string }).username);

    default:
      throw new Error(`Function ${functionName} not implemented`);
  }
};

// Check if a username is available
/** Determines whether the requested site ID (username) is available. */
const checkSiteId = async (siteId: string) => {
  try {
    const profileRef = doc(db, "profiles", siteId);
    const profileDoc = await getDoc(profileRef);

    return { available: !profileDoc.exists() };
  } catch (error) {
    console.error("Error checking username:", error);
    throw new Error(
      error instanceof Error ? error.message : "Unknown error occurred"
    );
  }
};
